import React, { type FC } from 'react';
import { useRouter } from 'expo-router';
import { CardShell } from './card-shell';
import { MaterialSymbol } from '../../icons/MaterialSymbol';
import type { ToolCardProps } from './types';
import { safeGet, safeArray, asArray } from './types';

const MAX_DISPLAY = 8;

interface JobItem {
  id?: string;
  title?: string;
  company?: string | { name?: string; id?: string; logo?: string | null };
  companyName?: string;
  location?: string;
  type?: string;
  skills?: string[];
  matchScore?: number;
  matchedSkills?: string[];
  matchReasons?: string[];
}

export const JobListCard: FC<ToolCardProps> = ({ result }) => {
  const router = useRouter();
  const jobsRaw = safeArray<JobItem>(result, 'jobs');
  const jobsFromArray = asArray<JobItem>(result);
  const allJobs: JobItem[] = jobsRaw.length > 0 ? jobsRaw : jobsFromArray;
  const total = safeGet<number>(result, 'total') ?? allJobs.length;
  const jobs = allJobs.slice(0, MAX_DISPLAY);

  if (jobs.length === 0) return null;

  return (
    <CardShell wide>
      <div
        style={{
          overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          display: 'flex',
          gap: 10,
          padding: 18,
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {jobs.map((job, i) => {
          const companyObj = job.company;
          const company =
            (typeof companyObj === 'object' && companyObj?.name) ||
            (typeof companyObj === 'string' && companyObj) ||
            job.companyName ||
            'Company';
          const companyLogo =
            typeof companyObj === 'object' ? (companyObj?.logo ?? undefined) : undefined;
          const skills = (job.matchedSkills || job.skills || []).slice(0, 3);

          return (
            <button
              type="button"
              key={job.id ?? i}
              onClick={job.id ? () => router.push(('/(app)/jobs/' + job.id) as never) : undefined}
              disabled={!job.id}
              style={{
                scrollSnapAlign: 'start',
                flexShrink: 0,
                width: 260,
                borderRadius: 20,
                padding: 18,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                position: 'relative',
                background: 'rgba(0,0,0,0.04)',
                cursor: job.id ? 'pointer' : 'default',
                textAlign: 'start',
                fontFamily: 'inherit',
                border: 0,
                transition: 'background-color 120ms',
                overflow: 'hidden',
              }}
              onMouseEnter={(e) => {
                if (job.id) e.currentTarget.style.background = 'rgba(0,0,0,0.06)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(0,0,0,0.04)';
              }}
            >
              {/* Match score badge */}
              {job.matchScore != null && (
                <span
                  className="tabbio-menu-badge"
                  style={{
                    position: 'absolute',
                    top: 12,
                    insetInlineEnd: 12,
                  }}
                >
                  {Math.round(job.matchScore * 100)}%
                </span>
              )}

              {/* Company row */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  width: '100%',
                  minWidth: 0,
                  paddingInlineEnd: job.matchScore != null ? 46 : 0,
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: 'rgba(0,0,0,0.06)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--text-tertiary)',
                    flexShrink: 0,
                    overflow: 'hidden',
                  }}
                >
                  {companyLogo ? (
                    <img
                      src={companyLogo}
                      alt={company}
                      referrerPolicy="no-referrer"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <MaterialSymbol name="business" size={14} />
                  )}
                </div>
                <span
                  dir="auto"
                  style={{
                    fontSize: 12,
                    color: 'var(--text-tertiary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {company}
                </span>
              </div>

              {/* Job title */}
              <div
                dir="auto"
                style={{
                  fontSize: 15,
                  fontWeight: 500,
                  color: 'var(--text-primary)',
                  lineHeight: '20px',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {job.title || 'Untitled Position'}
              </div>

              {/* Location + Type */}
              {(job.location || job.type) && (
                <div
                  style={{
                    fontSize: 12,
                    color: 'var(--text-tertiary)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  {job.location && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <MaterialSymbol name="location_on" size={12} />
                      {job.location}
                    </span>
                  )}
                  {job.location && job.type && <span style={{ opacity: 0.5 }}>·</span>}
                  {job.type && <span>{job.type}</span>}
                </div>
              )}

              {/* Skills pills */}
              {skills.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 2 }}>
                  {skills.map((skill, si) => (
                    <span key={si} className="tabbio-menu-badge">
                      {skill}
                    </span>
                  ))}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Footer */}
      <div
        style={{
          padding: '0 18px 14px',
          fontSize: 12,
          color: 'var(--text-tertiary)',
        }}
      >
        Showing {jobs.length} of {total} result{total !== 1 ? 's' : ''}
      </div>
    </CardShell>
  );
};
